import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import {
  AppsSettingsOauthRedirectSource,
  HUBSPOT_OAUTH_URL,
} from '@/constants/auth';
import {
  API_STATUS_FAILED,
  CODE_MISSING_INPUT,
  CODE_VALIDATION_ERROR,
} from '@/constants/error-codes';
import { SECOND_MS } from '@/constants/time';
import { useDesktopAutoConnect } from '@/desktop/providers/DesktopAutoConnectProvider';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { listTeamTrialsQueryCreator } from '@/query/listTeamTrialsQueryCreator';
import { teamAppsQueryCreator } from '@/query/teamAppsQueryCreator';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { userWorkspaceQueryCreator } from '@/query/userWorkspaceQueryCreator';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';
import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import { openOAuthUrl } from '@/shared/utils/auth';
import { isFullUrl } from '@/shared/utils/urlUtils';
import { autofillConfigsQueryCreator } from '../query/autofillConfigsQueryCreator';
import { crmConnectionUsageQueryCreator } from '../query/crmConnectionUsageQueryCreator';
import { getConnectedAppsQueryCreator } from '../query/getConnectedAppsQueryCreator';
import { trackCrmMain } from '../tracking/crm-tracking';
import {
  hubspotRedirectUriFull,
  hubspotRedirectUriPath,
} from '../utils/hubspot';

export type ConnectHubspotToOtterAccountVars = {
  /**
   * The code returned from the HubSpot OAuth callback.
   */
  code: string;
  /**
   * The redirect URL to redirect to after the connection is made.
   */
  redirectUrl: string;
  /**
   * The HubSpot instance URL
   */
  instanceUrl: string;
  /**
   * Reuse an existing connection between HubSpot and Otter
   */
  useExistingLink?: boolean;
  /**
   * Is this connecting a HubSpot account to an Otter user
   */
  isMemberLink: boolean;
};

/**
 * Get functions to deal with HubSpot OAuth
 */
export type HubspotOAuthHook = {
  /**
   * Opens the HubSpot OAuth page for the user to connect their HubSpot account to Otter.
   */
  openHubspotAuthorizationPage: (args: {
    connectionType: 'member' | 'all';
    redirectUri?: string;
    hubId?: string;
  }) => void;
  /**
   * After the HubSpot OAuth redirects back to Otter, call this function to
   * connect the user's HubSpot account to Otter.
   */
  connectHubspotToOtterAccount: (
    vars: ConnectHubspotToOtterAccountVars,
  ) => Promise<void>;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  /**
   * Call this function to prefetch data needed to open the HubSpot authorization page.
   */
  prefetchData: () => void;
  disconnectHubspot: () => Promise<void>;
  /**
   * Connect all HubSpot accounts for the workspace without opening the OAuth flow again.
   * This re-uses an existing HubSpot <> Otter link (if present) by calling the same
   * underlying REST endpoint that the classic web app uses.
   */
  connectAllHubspotAccounts: () => Promise<void>;
  /**
   * Callback fired when the user clicks the confirm button.
   * The new settings are passed as the only argument.
   */
  onConfirmConfig: (isMemberLink: boolean) => Promise<void>;
};

export function useHubspotOAuth(): HubspotOAuthHook {
  const queryClient = useQueryClient();
  const prefetchDataRef = useRef(false);
  const { userId } = useGetCurrentUser();

  const prefetchData = () => {
    if (prefetchDataRef.current) {
      return;
    }
    prefetchDataRef.current = true;
    setTimeout(() => {
      queryClient.prefetchQuery(teamAppsQueryCreator());
      queryClient.prefetchQuery(listTeamTrialsQueryCreator());
    }, 15 * SECOND_MS);
  };

  const {
    mutateAsync: connectHubspotToOtterAccount,
    isPending,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: async (vars: ConnectHubspotToOtterAccountVars) => {
      const results = await connectHubspot({
        ...vars,
      });
      return results;
    },
    onSuccess: () => {
      trackCrmMain.connectionEstablished('hubspot');
      Promise.allSettled([
        queryClient.invalidateQueries(teamAppsQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(userWorkspaceQueryCreator()),
        queryClient.invalidateQueries(workspaceQueryCreator()),
        queryClient.invalidateQueries(listTeamTrialsQueryCreator()),
        // Invalidate autofill configs so settings reflect new HubSpot autofill availability
        queryClient.invalidateQueries(
          autofillConfigsQueryCreator({ destination_service: 'hubspot' }),
        ),
        queryClient.invalidateQueries(crmConnectionUsageQueryCreator()),
      ]);
    },
    onError: () => {
      trackCrmMain.connectionFailed('hubspot');
    },
  });

  const openHubspotAuthorizationPage = useCallback(
    async ({
      connectionType,
      redirectUri,
      hubId,
    }: {
      connectionType: 'member' | 'all';
      redirectUri?: string;
      hubId?: string;
    }) => {
      const { workspace } =
        (await queryClient.fetchQuery(workspaceQueryCreator())) ?? {};

      if (!workspace) {
        return;
      }

      if (!userId) {
        throw new Error('User ID is not available');
      }

      goToHubspotAuthorizationPage({
        userId,
        isMember: connectionType === 'member',
        redirectUri,
        hubId,
      });
    },
    [queryClient, userId],
  );

  const { mutateAsync: disconnectHubspot } = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      const response = await legacyRestFetch('delete_hubspot_oauth', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new FailedToDisconnectHubspotError('Unable to make connection');
      }
      const body = await response.json();
      if (body?.status === API_STATUS_FAILED) {
        throw new FailedToDisconnectHubspotError(
          body?.message ?? 'API failed to disconnect',
        );
      }
    },
    onSuccess: () =>
      Promise.allSettled([
        queryClient.invalidateQueries(teamAppsQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(userWorkspaceQueryCreator()),
        queryClient.invalidateQueries(workspaceQueryCreator()),
        queryClient.invalidateQueries(crmConnectionUsageQueryCreator()),
      ]),
  });

  /**
   * Helper to connect all HubSpot accounts without redirecting to an OAuth page. This mirrors
   * the behavior implemented in the legacy Angular component (onClickConnectAllToHubspot).
   * It calls the same POST `hubspot_oauth` endpoint with an empty `authorization_code` but
   * sets `use_existing_link=true` so that the backend re-uses the previously authorized link.
   */
  const connectAllHubspotAccounts = useCallback(async () => {
    const redirectUrl = hubspotRedirectUriFull(false);

    await connectHubspotToOtterAccount({
      code: '',
      redirectUrl,
      instanceUrl: typeof document !== 'undefined' ? document.referrer : '',
      useExistingLink: true,
      isMemberLink: false,
    });
  }, [connectHubspotToOtterAccount]);

  const onConfirmConfig = useCallback(
    async (isMemberLink: boolean) => {
      await connectHubspotToOtterAccount({
        code: '',
        redirectUrl: hubspotRedirectUriFull(isMemberLink),
        instanceUrl: typeof document !== 'undefined' ? document.referrer : '',
        useExistingLink: true,
        isMemberLink: isMemberLink,
      });
    },
    [connectHubspotToOtterAccount],
  );

  // Auto-trigger OAuth if desktop app requested connecting this app
  useDesktopAutoConnect(AppsSettingsOauthRedirectSource.HUBSPOT, () =>
    openHubspotAuthorizationPage({ connectionType: 'member' }),
  );

  return {
    openHubspotAuthorizationPage,
    connectHubspotToOtterAccount,
    isPending,
    isSuccess,
    isError,
    prefetchData,
    disconnectHubspot,
    connectAllHubspotAccounts,
    onConfirmConfig,
  };
}

function goToHubspotAuthorizationPage({
  userId,
  isMember,
  redirectUri,
  hubId,
}: {
  userId: string | number;
  isMember: boolean;
  redirectUri?: string;
  hubId?: string;
}): void {
  const uri = new URL(
    redirectUri ?? hubspotRedirectUriPath(isMember),
    location.origin,
  );
  openHubspotOAuth(userId.toString(), uri.toString(), hubId);
}

function openHubspotOAuth(
  userId: string,
  redirectUri?: string,
  hubId?: string,
): void {
  const url = new URL(HUBSPOT_OAUTH_URL);
  url.searchParams.set('client_id', getHubspotClientId());

  if (redirectUri) {
    url.searchParams.set('redirect_uri', redirectUri);
  }

  url.searchParams.set('scope', getHubspotScope());
  url.searchParams.set('optional_scope', getHubspotOptionalScope());

  if (hubId) {
    url.searchParams.set('hub_id', hubId);
  }

  url.searchParams.set('state', btoa(`${userId}+${Date.now()}`));
  url.searchParams.set('response_type', 'code');

  openOAuthUrl(url.toString(), AppsSettingsOauthRedirectSource.HUBSPOT);
}

async function connectHubspot({
  code,
  redirectUrl,
  instanceUrl,
  useExistingLink,
  isMemberLink,
}: ConnectHubspotToOtterAccountVars) {
  const body = new FormData();
  if (isMemberLink && !code) {
    throw new MissingHubspotAuthorizationCodeError();
  }

  if (!redirectUrl) {
    throw new MissingHubspotRedirectUrlError();
  }

  if (!isFullUrl(redirectUrl)) {
    throw new InvalidHubspotRedirectUrlError(redirectUrl);
  }

  body.append('authorization_code', code);
  body.append('instance_url', instanceUrl);
  body.append('redirect_uri', redirectUrl);
  body.append('is_member_link', isMemberLink.toString());
  if (useExistingLink !== undefined) {
    body.append('use_existing_link', useExistingLink.toString());
  }

  const response = await legacyRestFetch('hubspot_oauth', {
    method: 'POST',
    body,
  });

  if (!response.ok) {
    throw new FailedToConnectHubspotToOtterAccountError(
      'Failed to connect to API',
    );
  }

  if (response.status !== 200) {
    throw response.body;
  }

  const results = await response.json();

  if (results.status === API_STATUS_FAILED) {
    if (results.code === CODE_MISSING_INPUT) {
      throw new FailedToConnectHubspotToOtterAccountError(
        'Missing input',
        results.code,
        results,
      );
    }
    if (results.code === CODE_VALIDATION_ERROR) {
      throw new FailedToConnectHubspotToOtterAccountError(
        'Validation error',
        results.code,
        results,
      );
    }
    throw new FailedToConnectHubspotToOtterAccountError(
      'Failed to connect HubSpot to Otter account',
      results.code,
      results,
    );
  }

  return results;
}

function getHubspotClientId() {
  if (!process.env.NEXT_PUBLIC_HUBSPOT_CLIENT_ID) {
    throw new MissingHubspotConfigurationError('Client ID');
  }
  return process.env.NEXT_PUBLIC_HUBSPOT_CLIENT_ID;
}

function getHubspotScope() {
  if (!process.env.NEXT_PUBLIC_HUBSPOT_SCOPE) {
    throw new MissingHubspotConfigurationError('Scope');
  }
  return process.env.NEXT_PUBLIC_HUBSPOT_SCOPE;
}

function getHubspotOptionalScope() {
  if (!process.env.NEXT_PUBLIC_HUBSPOT_OPTIONAL_SCOPE) {
    throw new MissingHubspotConfigurationError('Optional Scope');
  }
  return process.env.NEXT_PUBLIC_HUBSPOT_OPTIONAL_SCOPE;
}

class MissingHubspotConfigurationError extends Error {
  name = 'MissingHubspotConfigurationError';

  constructor(missingConfig: string) {
    super(`Missing HubSpot configuration: ${missingConfig}`);
  }
}

class FailedToConnectHubspotToOtterAccountError extends Error {
  name = 'FailedToConnectHubspotToOtterAccountError';

  constructor(
    message: string,
    public errorCode?: number,
    public source?: unknown,
  ) {
    super(message);
  }
}

class FailedToDisconnectHubspotError extends Error {
  name = 'FailedToDisconnectHubspotError';
}

class MissingHubspotAuthorizationCodeError extends Error {
  name = 'MissingHubspotAuthorizationCodeError';
  message = 'HubSpot authorization code is required';
}

class MissingHubspotRedirectUrlError extends Error {
  name = 'MissingHubspotRedirectUrlError';
  message = 'HubSpot redirect URL is required';
}

class InvalidHubspotRedirectUrlError extends Error {
  name = 'InvalidHubspotRedirectUrlError';

  constructor(redirectUrl: string) {
    super(`HubSpot redirect URL is invalid: ${redirectUrl}`);
  }
}
